import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Clock, TrendingUp, Briefcase, UploadCloud, X, Loader2, CheckCircle2, Users } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { usePublicApplication } from '@/hooks/usePublicApplication';
import { HiroMark } from '@/components/auth/AuthMarketingPanel';
import { HiroMatchLogo } from '@/components/branding/HiroMatchLogo';
import { translateForLanguage, type Language } from '@/contexts/LanguageContext';
import { normalizeJobLocation } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { PageSeo } from '@/components/seo/PageSeo';
import { DEFAULT_OG_IMAGE, NOINDEX_FOLLOW } from '@/lib/seo/constants';
import { excerpt } from '@/lib/seo/excerpt';

function translateEnumLabel(
  language: Language,
  prefix: string,
  value: string | undefined,
): string | undefined {
  if (!value) return undefined;
  const key = `${prefix}.${value}`;
  const translated = translateForLanguage(language, key);
  return translated === key ? value : translated;
}

function translateWorkMode(value: string | undefined, language: Language): string | undefined {
  return translateEnumLabel(language, 'apply.workMode', value);
}

function translateContractType(value: string | undefined, language: Language): string | undefined {
  return translateEnumLabel(language, 'apply.contractType', value);
}

function translateSeniority(value: string | undefined, language: Language): string | undefined {
  return translateEnumLabel(language, 'apply.seniority', value);
}

function PublicApplyPoweredByFooter({
  t,
  className = 'pb-6',
}: {
  t: (key: string) => string;
  className?: string;
}) {
  return (
    <a
      href="https://hiromatch.com"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors ${className}`}
    >
      <span>{t('apply.poweredBy')}</span>
      <HiroMark className="h-4 w-4" />
      <strong className="font-semibold tracking-tight">HiroMatch</strong>
    </a>
  );
}

function PublicApplyStatusMessage({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        <HiroMatchLogo className="h-16 w-16 mx-auto" />
        <p className="font-display font-bold text-2xl tracking-tight text-foreground mt-4">
          HiroMatch
        </p>
        <h1 className="text-xl font-semibold mt-6 mb-2">{title}</h1>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
    </div>
  );
}

const PublicPostingPage = () => {
  const { companySlug, postingSlug } = useParams();
  const location = useLocation();
  const hook = usePublicApplication(companySlug, postingSlug);
  const { tPage: t } = hook;
  const isIt = hook.pageLanguage === 'it';

  const applySeoTitle =
    hook.position?.title && hook.company?.publicName
      ? `${hook.position.title} — ${hook.company.publicName} | HiroMatch`
      : isIt
        ? 'Candidatura — HiroMatch'
        : 'Apply — HiroMatch';
  const applySeoDescription = hook.position?.description
    ? excerpt(hook.position.description)
    : undefined;
  const applyOgImage = hook.company?.logoUrl || DEFAULT_OG_IMAGE;
  const applySeo = (
    <PageSeo
      title={applySeoTitle}
      description={applySeoDescription}
      path={location.pathname}
      robots={NOINDEX_FOLLOW}
      ogImage={applyOgImage}
    />
  );

  if (hook.pageState === 'loading') {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        {applySeo}
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">{t('apply.loading')}</p>
        </div>
      </div>
    );
  }

  if (hook.pageState === 'not_found') {
    return (
      <>
        {applySeo}
      <PublicApplyStatusMessage
        title={t('apply.notFound')}
        description={t('apply.notFoundDesc')}
      />
      </>
    );
  }

  if (hook.pageState === 'closed') {
    return (
      <>
        {applySeo}
      <PublicApplyStatusMessage
        title={t('apply.closed')}
        description={t('apply.closedDesc')}
      />
      </>
    );
  }

  if (hook.pageState === 'success') {
    // Sprint 1 (D-R3.3 / BUG-10): success screen now mirrors the brand
    // language used everywhere else — HiroMark + "HiroMatch" wordmark, both
    // in the success card header and in the powered-by footer.
    return (
      <div className="min-h-screen bg-muted/30 flex flex-col items-center justify-center px-4">
        {applySeo}
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-xl font-semibold">{t('apply.success')}</h1>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>{t('apply.thankYou')} <strong>{hook.firstName} {hook.lastName}</strong>,</p>
              <p>{t('apply.received', { position: hook.position?.title || '', company: hook.company?.publicName || '' })}</p>
            </div>
          </CardContent>
        </Card>

        {/* Powered-by — branded footer */}
        <PublicApplyPoweredByFooter t={t} className="mt-6 pb-6" />
      </div>
    );
  }

  const { company, position } = hook;
  const lang = hook.pageLanguage;
  const workModeLabel = translateWorkMode(position?.workMode, lang);
  const contractTypeLabel = translateContractType(position?.contractType, lang);
  const seniorityLabel = translateSeniority(position?.seniority, lang);
  const publicLocation = normalizeJobLocation(position?.location);

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background">
      {applySeo}
      <div className="max-w-2xl mx-auto px-4 py-8 md:py-16 pb-10 md:pb-14 w-full">
        {/* Company Header */}
        <div className="text-center mb-4">
          <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
            {company?.logoUrl ? (
              <img src={company.logoUrl} alt={`${company.publicName} logo`} className="h-10 w-10 rounded-lg object-cover" />
            ) : (
              <span className="text-lg font-bold text-primary">{company?.publicName?.[0]}</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-1">{company?.publicName}</p>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{position?.title}</h1>

          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {workModeLabel && (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                <MapPin className="h-3 w-3" /> {workModeLabel}
              </span>
            )}
            {publicLocation && (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                <MapPin className="h-3 w-3" /> {publicLocation}
              </span>
            )}
            {contractTypeLabel && (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                <Clock className="h-3 w-3" /> {contractTypeLabel}
              </span>
            )}
            {seniorityLabel && (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                <TrendingUp className="h-3 w-3" /> {seniorityLabel}
              </span>
            )}
            {position?.salaryMin && position?.salaryMax && (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                <Briefcase className="h-3 w-3" /> €{Math.round(position.salaryMin / 1000)}k – €{Math.round(position.salaryMax / 1000)}k
              </span>
            )}
          </div>
        </div>

        <Card className="mb-8 border-border/50">
          <CardContent className="pt-6">
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown>{position?.description || ''}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>

        {(company?.description || company?.employeeCount) && (
          <Card className="mb-8 border-border/50">
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-3">{t('apply.about', { company: company.publicName })}</h2>
              {company.description && <p className="text-sm text-muted-foreground mb-4">{company.description}</p>}
              <div className="flex flex-wrap gap-3">
                {company.employeeCount && (
                  <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                    <Users className="h-3 w-3" /> {t('apply.employees', { count: company.employeeCount })}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-border/50">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-5">{t('apply.submitTitle')}</h2>

            {hook.serverError && (
              <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm">{hook.serverError}</div>
            )}

            <form onSubmit={hook.handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('apply.firstName')}</Label>
                  <Input value={hook.firstName} onChange={e => hook.setFirstName(e.target.value)} placeholder={isIt ? 'Mario' : 'John'} className={hook.errors.firstName ? 'border-destructive' : ''} />
                  {hook.errors.firstName && <p className="text-xs text-destructive">{hook.errors.firstName}</p>}
                </div>
                <div className="space-y-2">
                  <Label>{t('apply.lastName')}</Label>
                  <Input value={hook.lastName} onChange={e => hook.setLastName(e.target.value)} placeholder={isIt ? 'Rossi' : 'Doe'} className={hook.errors.lastName ? 'border-destructive' : ''} />
                  {hook.errors.lastName && <p className="text-xs text-destructive">{hook.errors.lastName}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('apply.email')}</Label>
                <Input type="email" value={hook.email} onChange={e => hook.setEmail(e.target.value)} placeholder={isIt ? 'mario.rossi@email.com' : 'john.doe@email.com'} className={hook.errors.email ? 'border-destructive' : ''} />
                {hook.errors.email && <p className="text-xs text-destructive">{hook.errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label>{t('apply.phone')}{position?.requirePhone === true && <span className="text-destructive ml-0.5">*</span>}</Label>
                <Input type="tel" value={hook.phone} onChange={e => hook.setPhone(e.target.value)} placeholder={isIt ? '+39 333 1234567' : '+1 234 567 8900'} className={hook.errors.phone ? 'border-destructive' : ''} />
                {hook.errors.phone && <p className="text-xs text-destructive">{hook.errors.phone}</p>}
              </div>

              <div className="space-y-2">
                <Label>{t('apply.linkedIn')}{position?.requireLinkedIn === true && <span className="text-destructive ml-0.5">*</span>}</Label>
                <Input type="url" value={hook.linkedIn} onChange={e => hook.setLinkedIn(e.target.value)} placeholder="https://linkedin.com/in/..." className={hook.errors.linkedIn ? 'border-destructive' : ''} />
                {hook.errors.linkedIn && <p className="text-xs text-destructive">{hook.errors.linkedIn}</p>}
              </div>

              <div className="space-y-2">
                <Label>{t('apply.resume')}</Label>
                {hook.cvFile ? (
                  <div className="flex items-center gap-3 p-3 rounded-md border border-border bg-muted/30">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{hook.cvFile.name}</p>
                      <p className="text-xs text-muted-foreground">{(hook.cvFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => hook.setCvFile(null)} aria-label={isIt ? 'Rimuovi curriculum' : 'Remove resume'}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors ${hook.errors.cv ? 'border-destructive' : 'border-border'}`}
                    onClick={() => hook.fileInputRef.current?.click()}
                    onDragOver={e => e.preventDefault()}
                    onDrop={hook.handleFileDrop}
                  >
                    <UploadCloud className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">{t('apply.dragResume')}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">{t('apply.pdfMax')}</p>
                  </div>
                )}
                {hook.errors.cv && <p className="text-xs text-destructive">{hook.errors.cv}</p>}
                <input ref={hook.fileInputRef} type="file" accept=".pdf" className="hidden" onChange={hook.handleFileSelect} />
              </div>

              <Button type="submit" className="w-full" disabled={hook.submitting}>
                {hook.submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('apply.submitting')}</> : t('apply.submit')}
              </Button>

              <p className="text-xs text-muted-foreground text-center">{t('apply.gdpr')}</p>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <PublicApplyPoweredByFooter t={t} className="pb-0" />
        </div>
      </div>
    </div>
  );
};

export default PublicPostingPage;
